import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CiTrash } from 'react-icons/ci';

const BASE_URL = "https://api.jeyshawnng.com";

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [filter, setFilter]     = useState('all'); // all | unread
  const [deletingId, setDeletingId] = useState(null);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${BASE_URL}/api/contact/messages`, authHeaders());
      setMessages(data?.data || []);
    } catch (err) {
      console.error('Failed to fetch contact messages:', err);
      setError('Failed to load messages.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const markRead = async (id) => {
    setMessages(prev => prev.map(m => (m._id === id ? { ...m, isRead: true } : m)));
    try {
      await axios.patch(`${BASE_URL}/api/contact/messages/${id}/read`, {}, authHeaders());
    } catch (err) {
      console.error('Failed to mark message read:', err);
      fetchMessages();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    setDeletingId(id);
    try {
      await axios.delete(`${BASE_URL}/api/contact/messages/${id}`, authHeaders());
      setMessages(prev => prev.filter(m => m._id !== id));
    } catch (err) {
      console.error('Failed to delete message:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const displayed = filter === 'unread' ? messages.filter(m => !m.isRead) : messages;
  const unreadCount = messages.filter(m => !m.isRead).length;

  if (loading) return <p style={{ padding: 20 }}>Loading messages...</p>;
  if (error) {
    return (
      <div style={{ padding: 20 }}>
        <p style={{ color: '#dc2626' }}>{error}</p>
        <button onClick={fetchMessages}>Retry</button>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, maxWidth: 800 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>
          Contact Messages {unreadCount > 0 && (
            <span style={{
              background: '#dc2626', color: '#fff', fontSize: 12, borderRadius: 12,
              padding: '2px 8px', marginLeft: 8, verticalAlign: 'middle',
            }}>
              {unreadCount} new
            </span>
          )}
        </h2>

        {/* ── Filter toggle ── */}
        <div style={{
          display: 'inline-flex',
          background: '#f2f2f2',
          borderRadius: 8,
          padding: 3,
          gap: 2,
        }}>
          <button
            onClick={() => setFilter('all')}
            style={{
              border: 'none',
              cursor: 'pointer',
              padding: '6px 16px',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              background: filter === 'all' ? '#fff' : 'transparent',
              color: filter === 'all' ? '#111' : '#777',
              boxShadow: filter === 'all' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
              transition: 'background 0.15s, color 0.15s, box-shadow 0.15s',
            }}
          >
            All
          </button>
          <button
            onClick={() => setFilter('unread')}
            style={{
              border: 'none',
              cursor: 'pointer',
              padding: '6px 16px',
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              background: filter === 'unread' ? '#fff' : 'transparent',
              color: filter === 'unread' ? '#111' : '#777',
              boxShadow: filter === 'unread' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
              transition: 'background 0.15s, color 0.15s, box-shadow 0.15s',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            Unread
            {unreadCount > 0 && (
              <span style={{
                background: filter === 'unread' ? '#dc2626' : '#bbb',
                color: '#fff',
                fontSize: 10,
                fontWeight: 700,
                borderRadius: 10,
                minWidth: 16,
                height: 16,
                padding: '0 4px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {displayed.length === 0 && (
        <p style={{ color: '#888' }}>No messages{filter === 'unread' ? ' (all caught up).' : ' yet.'}</p>
      )}

      {displayed.map((m) => (
        <div
          key={m._id}
          onClick={() => !m.isRead && markRead(m._id)}
          style={{
            border: '1px solid #eee',
            borderLeft: m.isRead ? '3px solid transparent' : '3px solid #dc2626',
            borderRadius: 6,
            padding: '14px 16px',
            marginBottom: 10,
            cursor: m.isRead ? 'default' : 'pointer',
            background: m.isRead ? '#fff' : '#fff7f7',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <strong>{m.name}</strong>
              <span style={{ color: '#666', marginLeft: 8, fontSize: 13 }}>{m.email}</span>
              {m.phone && <span style={{ color: '#666', marginLeft: 8, fontSize: 13 }}>· {m.phone}</span>}
              {m.company && <span style={{ color: '#666', marginLeft: 8, fontSize: 13 }}>· {m.company}</span>}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); handleDelete(m._id); }}
              disabled={deletingId === m._id}
              title="Delete"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999' }}
            >
              <CiTrash size={18} />
            </button>
          </div>
          <p style={{ margin: '8px 0 4px', color: '#333' }}>{m.message}</p>
          <small style={{ color: '#aaa' }}>{new Date(m.createdAt).toLocaleString()}</small>
        </div>
      ))}
    </div>
  );
};

export default ContactMessages;